import React, { useEffect, useState } from 'react';
import { useHistory, useParams, Redirect } from 'react-router-dom';
import { useIsFetching } from '@tanstack/react-query';
import OptionSelectionContent from '../../OptionSelection/OptionSelectionContent';
import YearbookPoseSelection from '../YearbookPose/YearbookPoseSelection';
import useRetouchingExtra from '../../../../hooks/useRetouchingExtra';
import { useCustomer } from '../../../../contexts/CustomerContext';
import useBackgrounds from '../../../../hooks/useBackgrounds';
import { OrderPackage } from '../../../../models';
import usePoses from '../../../../hooks/usePoses';
import usePricelist from '../../../../hooks/usePricelist';
import useAddons from '../../../../hooks/useAddons';
import routes from '../../../../routes';
import useQueryString from '../../../../hooks/use-query-string';
import QueryKeys from '../../../../utils/QueryKeys';
import SeniorMultiPose from '../../../PackageBuilders/MultiPose/SeniorMultiPose';
import usePreorderPose from "../../../../hooks/usePreorderPose";
import useHolidayPricelist from "../../../../hooks/useHolidayPricelist";

export default function Builder() {
  // Routing and queries
  const { packageId } = useParams();
  const history = useHistory();
  const { step, opt: optionId } = useQueryString();
  const { preorderPoseId } = useQueryString();

  // Data
  const { customer, setCustomer } = useCustomer();
  const [orderPackage] = useState(new OrderPackage());
  const { data: backgrounds } = useBackgrounds(packageId);
  const { data: poses } = usePoses(customer.currentStudent.id, packageId);
  const { data: preorderPose } = usePreorderPose(preorderPoseId)
  const { data: pricelist } = usePricelist(packageId);
  const { data: giftPricelist } = useHolidayPricelist(customer.currentSchool?.id);

  useRetouchingExtra();
  // Senior flow — fetch SENIOR add-ons. useAddons() defaults to elementary,
  // which leaked elementary add-ons (e.g. the $19.95 single digital file) into
  // the senior portrait flow.
  const { data: addons } = useAddons(false);

  // UI
  const isFetchingPoses = useIsFetching({ queryKey: [QueryKeys.STUDENT_IMAGES] });
  const isFetchingBackgrounds = useIsFetching({ queryKey: [QueryKeys.BACKGROUNDS] });
  const isFetchingPricelist = useIsFetching({ queryKey: [QueryKeys.PRICELIST] });

  const isFetching = isFetchingPoses || isFetchingBackgrounds || isFetchingPricelist;

  const onSave = () => {
    setCustomer({ ...customer, currentOrderPackage: orderPackage });

    if (giftPricelist) history.push(routes.holiday.builder({ optional: true, o: orderPackage.id, pid: orderPackage?.packageId }))
    else if (addons.length === 0) history.push(routes.retouching(orderPackage.id));
    else history.push(routes.seniors.addons(orderPackage.id));
  };

  useEffect(() => {
    document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'none'; });

    return () => document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'flex'; });
  }, []);

  if (!(packageId && customer.currentSchool && customer.currentStudent)) return null;
  if (isFetching) return null;
  if (backgrounds.length === 0) return null;

  const background = backgrounds[0];

  // The senior flow now LEADS with the yearbook-pose pick (all photos shown), then
  // packages, then the builder — there is no "favorites" pre-selection step. The
  // chosen yearbook pose (Pose#is_yearbook_pose) is the hero image for the package
  // upsell and sorts first in the build step.
  const yearbookPose = poses.find((pose) => pose.isYearbookPose === true);
  const hasYearbookPose = !!yearbookPose;

  // Required-yearbook gate: a senior cannot reach packages/build/checkout without a
  // confirmed yearbook pose. If they land on select-package or build without one,
  // send them back to the yearbook step. Use <Redirect> rather than history.replace
  // so the navigation is render-safe (no state update in render).
  if ((step === 'select-package' || step === 'build') && !hasYearbookPose) {
    return <Redirect to={routes.seniors.packageBuilder(packageId, { step: 'yearbook-pose' })} />;
  }

  return (
    <div>
      {(!step || step === 'yearbook-pose') && (
        <YearbookPoseSelection
          backgrounds={backgrounds}
          background={background}
          poses={poses}
          packageId={packageId}
        />
      )}
      {step === 'select-package' && pricelist && (
        <OptionSelectionContent
          isElementary={false}
          heading="Choose your package"
          foreground={yearbookPose?.watermarkUrl || poses[0]?.watermarkUrl || pricelist.placeholderImage()}
          background={background}
          multiPoseHandler={(selectedOpt) => history.push(routes.seniors.packageBuilder(packageId, { opt: selectedOpt.id, step: 'build' }))}
          packageId={packageId}
          accessCodeOnly={false}
          // Senior upsell: the $0 yearbook line is now its own step, so drop the
          // yearbook Option card here and offer a "just my yearbook photo" skip that
          // goes straight to review/checkout with the already-added $0 line.
          hideYearbookOption
          onSkip={() => history.push(routes.review())}
        />
      )}
      {step === 'build' && (
        <SeniorMultiPose
          cartId={customer.cart.id}
          packageId={packageId}
          schoolId={customer.currentSchool.id}
          studentId={customer.currentStudent.id}
          optionId={optionId}
          onSave={onSave}
          favoriteIds={yearbookPose ? [yearbookPose.id] : []}
          orderPackage={orderPackage}
        />
      )}
    </div>
  );
}
